// Vendor Portal — a self-contained app mounted at /vendor/*. A vendor logs in
// once and works the tickets assigned to them ACROSS every customer org they
// serve, plus manages technicians + shifts. Separate token/session from the
// operator app (vendor-api). Reuses the glass primitives + QuickFormModal.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Routes, Route, NavLink, Navigate, useNavigate, useParams,
} from 'react-router-dom';
import {
  LayoutDashboard, Ticket as TicketIcon, Users, UsersRound, CalendarClock, LogOut, Building2, AlertTriangle, Wrench,
} from 'lucide-react';
import { PageHeader } from '@/components/ui/DataTable';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { KpiStat, StatusPill, PriorityPill, Skeleton, EmptyState } from '@/components/ui/primitives';
import { QuickFormModal } from '@/components/QuickFormModal';
import { ToastProvider, useToast } from '@/components/ui/Toast';
import { Drawer } from '@/components/ui/Drawer';
import { LayoutGrid, Inbox, BarChart3, MapPin } from 'lucide-react';
import {
  vendorApi, getVendorToken, setVendorToken,
  type VendorSession, type VendorTicket, type VendorTechnician, type VendorTeam,
  type CalendarShift, type ShiftConflict, type Rotation, type Offer,
} from '@/lib/vendor-api';
import { VendorDispatchPage, VendorAnalyticsPage, VendorLocationsPage, VendorNotificationsPage, useManagerUnread } from './pages/manager-pages';
import { Bell } from 'lucide-react';

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });

export function VendorPortal() {
  return (
    <QueryClientProvider client={qc}>
      <ToastProvider>
        <VendorPortalInner />
      </ToastProvider>
    </QueryClientProvider>
  );
}

export type VendorMode = 'manager' | 'technician';
function modeFromRole(role: string | undefined): VendorMode {
  return role === 'manager' ? 'manager' : role === 'both' ? 'manager' : 'technician';
}

function VendorPortalInner() {
  const { t } = useTranslation(['vendor', 'common']);
  const [session, setSession] = useState<VendorSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getVendorToken()) { setLoading(false); return; }
    vendorApi.me().then(setSession).catch(() => setVendorToken(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grid h-screen place-items-center text-sm text-muted">{t('vendor:common.loading')}</div>;
  if (!session) return <VendorLogin onLogin={setSession} />;
  return <VendorShell session={session} onLogout={() => { vendorApi.logout(); setSession(null); }} />;
}

// ── Login ────────────────────────────────────────────────────────────────
function VendorLogin({ onLogin }: { onLogin: (s: VendorSession) => void }) {
  const { t } = useTranslation(['vendor', 'common']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await vendorApi.login(email, password);
      onLogin(await vendorApi.me());
    } catch {
      setErr(t('vendor:login.invalidCredentials'));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid h-screen place-items-center bg-surface-0 p-4">
      <form onSubmit={submit} className="tk-card w-full max-w-sm space-y-4 p-6">
        <div className="flex items-center gap-2 text-lg font-semibold"><Wrench size={20} className="text-primary" /> {t('vendor:portalName')}</div>
        <p className="text-xs text-muted">{t('vendor:login.subtitle')}</p>
        <input className="tk-input w-full" type="email" placeholder={t('vendor:login.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input className="tk-input w-full" type="password" placeholder={t('vendor:login.passwordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)} required />
        {err && <div className="text-xs text-critical">{err}</div>}
        <button className="tk-btn-primary w-full justify-center" disabled={busy}>{busy ? t('vendor:login.signingIn') : t('vendor:login.signIn')}</button>
        <p className="text-center text-[11px] text-muted/60">{t('vendor:login.inviteHint')}</p>
      </form>
    </div>
  );
}

// ── Shell ────────────────────────────────────────────────────────────────
// Role-based navigation. Managers get the operations surfaces; technicians get
// the field-work surfaces. A `both` login sees a mode switch and both sets.
const MANAGER_NAV = [
  { to: '/vendor/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { to: '/vendor/dispatch', labelKey: 'nav.dispatch', icon: Inbox },
  { to: '/vendor/queue', labelKey: 'nav.queue', icon: TicketIcon },
  { to: '/vendor/technicians', labelKey: 'nav.technicians', icon: Users },
  { to: '/vendor/teams', labelKey: 'nav.teams', icon: UsersRound },
  { to: '/vendor/calendar', labelKey: 'nav.calendar', icon: CalendarClock },
  { to: '/vendor/analytics', labelKey: 'nav.analytics', icon: BarChart3 },
  { to: '/vendor/locations', labelKey: 'nav.locations', icon: MapPin },
  { to: '/vendor/notifications', labelKey: 'nav.notifications', icon: Bell, badge: true as const },
];
const TECH_NAV = [
  { to: '/vendor/my-work', labelKey: 'nav.myWork', icon: Wrench, end: false },
  { to: '/vendor/my-tickets', labelKey: 'nav.myTickets', icon: TicketIcon },
  { to: '/vendor/schedule', labelKey: 'nav.schedule', icon: CalendarClock },
];

function VendorShell({ session, onLogout }: { session: VendorSession; onLogout: () => void }) {
  const { t } = useTranslation(['vendor', 'common']);
  const navigate = useNavigate();
  const canSwitch = session.role === 'both';
  const [mode, setMode] = useState<VendorMode>(() => modeFromRole(session.role));
  const nav = mode === 'manager' ? MANAGER_NAV : TECH_NAV;
  const switchMode = (m: VendorMode) => { setMode(m); navigate(m === 'manager' ? '/vendor/dashboard' : '/vendor/my-work'); };
  const unread = useManagerUnread();

  return (
    <div className="flex h-screen bg-surface-0 text-fg">
      <aside className="tk-chrome flex w-60 shrink-0 flex-col border-r border-white/10 p-3">
        <div className="mb-1 flex items-center gap-2 px-2 py-1 text-sm font-semibold">
          <Wrench size={18} className="text-primary" /> {t('vendor:portalName')}
        </div>
        <div className="mb-3 px-2 text-[11px] font-medium uppercase tracking-wide text-muted/70">
          {mode === 'manager' ? t('vendor:mode.manager', { defaultValue: 'Vendor Manager' }) : t('vendor:mode.technician', { defaultValue: 'Technician' })}
        </div>
        <nav className="flex-1 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={(n as { end?: boolean }).end}
              className={({ isActive }) => `flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors ${isActive ? 'bg-white/10 text-fg' : 'text-muted hover:text-fg hover:bg-white/5'}`}>
              <n.icon size={16} /> <span className="flex-1">{t(`vendor:${n.labelKey}`)}</span>
              {(n as { badge?: boolean }).badge && unread > 0 && (
                <span className="rounded-full bg-critical px-1.5 text-[10px] font-bold text-white">{unread > 99 ? '99+' : unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-3">
          {canSwitch && (
            <button onClick={() => switchMode(mode === 'manager' ? 'technician' : 'manager')}
              className="mb-2 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-primary hover:bg-white/5">
              <LayoutGrid size={16} /> {mode === 'manager' ? t('vendor:mode.toTech', { defaultValue: 'Switch to Technician' }) : t('vendor:mode.toManager', { defaultValue: 'Switch to Manager' })}
            </button>
          )}
          <div className="px-2 text-xs text-muted">{session.name}</div>
          <div className="px-2 text-[11px] text-muted/60">{session.email} · {t('vendor:shell.orgCount', { count: session.allowedTenantIds.length })}</div>
          <button onClick={onLogout} className="mt-2 flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted hover:bg-white/5 hover:text-fg">
            <LogOut size={16} /> {t('vendor:shell.signOut')}
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto p-6">
        <Routes>
          <Route path="/vendor" element={<Navigate to={mode === 'manager' ? '/vendor/dashboard' : '/vendor/my-work'} replace />} />
          <Route path="/vendor/dashboard" element={<VendorDashboardPage />} />
          <Route path="/vendor/dispatch" element={<VendorDispatchPage />} />
          <Route path="/vendor/my-work" element={<MyWorkPage session={session} />} />
          <Route path="/vendor/my-tickets" element={<MyWorkPage session={session} />} />
          <Route path="/vendor/schedule" element={<VendorCalendarPage session={session} />} />
          <Route path="/vendor/queue" element={<VendorQueuePage />} />
          <Route path="/vendor/tickets/:tenantId/:id" element={<VendorTicketPage />} />
          <Route path="/vendor/technicians" element={<VendorTechniciansPage session={session} />} />
          <Route path="/vendor/teams" element={<VendorTeamsPage session={session} />} />
          <Route path="/vendor/shifts" element={<VendorShiftsPage session={session} />} />
          <Route path="/vendor/calendar" element={<VendorCalendarPage session={session} />} />
          <Route path="/vendor/analytics" element={<VendorAnalyticsPage />} />
          <Route path="/vendor/locations" element={<VendorLocationsPage />} />
          <Route path="/vendor/notifications" element={<VendorNotificationsPage />} />
          <Route path="*" element={<Navigate to="/vendor" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// ── Summary ──────────────────────────────────────────────────────────────
function VendorSummaryPage() {
  const { t } = useTranslation(['vendor', 'common']);
  const { data } = useQuery({ queryKey: ['v-summary'], queryFn: vendorApi.summary, refetchInterval: 15000 });
  return (
    <div className="space-y-6">
      <PageHeader title={t('vendor:summary.title')} subtitle={t('vendor:summary.subtitle')} />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiStat label={t('vendor:summary.customers')} value={data?.orgs ?? '—'} tone="info" />
        <KpiStat label={t('vendor:summary.openTickets')} value={data?.open ?? '—'} tone="info" />
        <KpiStat label={t('vendor:summary.inProgress')} value={data?.inProgress ?? '—'} tone="warning" />
        <KpiStat label={t('vendor:summary.slaBreaching')} value={data?.breaching ?? '—'} tone={data && data.breaching > 0 ? 'critical' : 'ok'} />
      </div>
      <div className="tk-card">
        <div className="mb-3 text-sm font-medium">{t('vendor:summary.byCustomer')}</div>
        <div className="space-y-2">
          {(data?.byOrg ?? []).map((o) => (
            <div key={o.tenantId} className="flex items-center justify-between border-b border-white/5 py-2 text-sm last:border-0">
              <span className="flex items-center gap-2"><Building2 size={14} className="text-muted" /> {o.tenantName || o.tenantId.slice(0, 8)}</span>
              <span className="flex items-center gap-4 text-xs">
                <span className="text-muted">{t('vendor:summary.openCount', { count: o.open })}</span>
                {o.breaching > 0 && <span className="flex items-center gap-1 text-critical"><AlertTriangle size={12} /> {t('vendor:summary.breachingCount', { count: o.breaching })}</span>}
              </span>
            </div>
          ))}
          {(!data || data.byOrg.length === 0) && <div className="text-sm text-muted">{t('vendor:summary.noCustomers')}</div>}
        </div>
      </div>
    </div>
  );
}

// ── Queue (cross-org tickets) ──────────────────────────────────────────────
function VendorQueuePage() {
  const { t } = useTranslation(['vendor', 'common']);
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('');
  const { data: tickets, isLoading } = useQuery({
    queryKey: ['v-tickets', status], queryFn: () => vendorApi.tickets(status ? { status } : undefined), refetchInterval: 10000,
  });
  const STATUSES = ['', 'open', 'acknowledged', 'in_progress', 'on_hold', 'resolved', 'closed'];
  const columns: Column<VendorTicket>[] = [
    { key: 'ticketNumber', header: t('vendor:queue.col.ticket'), className: 'tk-num font-medium' },
    { key: 'title', header: t('vendor:queue.col.title') },
    { key: 'tenantName', header: t('vendor:queue.col.customer'), render: (t2) => <span className="flex items-center gap-1.5 text-muted"><Building2 size={13} /> {t2.tenantName || t2.tenantId.slice(0, 8)}</span> },
    { key: 'priority', header: t('vendor:queue.col.priority'), render: (t2) => <PriorityPill priority={t2.priority} /> },
    { key: 'status', header: t('vendor:queue.col.status'), render: (t2) => <StatusPill status={t2.status} label={t2.status} /> },
    { key: 'slaBreached', header: t('vendor:queue.col.sla'), render: (t2) => t2.slaBreached ? <span className="text-xs text-critical">{t('vendor:sla.breached')}</span> : <span className="text-xs text-muted">{t('vendor:sla.ok')}</span> },
  ];
  return (
    <div className="space-y-6">
      <PageHeader title={t('vendor:queue.title')} subtitle={t('vendor:queue.subtitle')} />
      <div className="flex gap-1.5">
        {STATUSES.map((s) => (
          <button key={s || 'all'} onClick={() => setStatus(s)}
            className={`rounded-md px-2.5 py-1 text-xs ring-1 ring-white/10 ${status === s ? 'bg-white/10 text-fg' : 'text-muted hover:text-fg'}`}>
            {s ? t(`vendor:status.${s}`) : t('vendor:status.all')}
          </button>
        ))}
      </div>
      {isLoading ? <Skeleton className="h-64" /> : (tickets ?? []).length === 0 ? (
        <EmptyState icon={TicketIcon} title={t('vendor:queue.empty.title')} hint={t('vendor:queue.empty.hint')} />
      ) : (
        <DataTable
          rows={(tickets ?? []).map((t2) => ({ ...t2, id: `${t2.tenantId}:${t2.id}` }))}
          columns={columns as Column<VendorTicket & { id: string }>[]}
          emptyIcon={TicketIcon} emptyTitle={t('vendor:queue.empty.title')}
          onRowClick={(t2) => navigate(`/vendor/tickets/${t2.tenantId}/${t2.id}`)}
        />
      )}
    </div>
  );
}

// ── Ticket detail (the work surface) — server-driven transitions + scored assign
const ALL_STATUSES = ['acknowledged', 'in_progress', 'on_hold', 'resolved', 'closed'];
function VendorTicketPage() {
  const { t: tr } = useTranslation(['vendor', 'common']);
  const { tenantId, id } = useParams<{ tenantId: string; id: string }>();
  const navigate = useNavigate();
  const qclient = useQueryClient();
  const [comment, setComment] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const { data: t, isLoading } = useQuery({ queryKey: ['v-ticket', tenantId, id], queryFn: () => vendorApi.ticket(tenantId!, id!) });

  const patch = async (body: Parameters<typeof vendorApi.patchTicket>[2]) => {
    await vendorApi.patchTicket(tenantId!, id!, body);
    qclient.invalidateQueries({ queryKey: ['v-ticket', tenantId, id] });
    qclient.invalidateQueries({ queryKey: ['v-tickets'] });
  };

  if (isLoading || !t) return <Skeleton className="h-64" />;
  // Server-driven next statuses; fall back to the full set if absent.
  const nextStatuses = (t.allowedTransitions && t.allowedTransitions.length ? t.allowedTransitions : ALL_STATUSES);
  const site = t.site;
  return (
    <div className="space-y-5">
      <PageHeader title={`${t.ticketNumber} · ${t.title}`} subtitle={t.tenantName}
        action={<button className="tk-btn-ghost" onClick={() => navigate(-1)}>← {tr('vendor:ticket.backToQueue')}</button>} />
      <div className="tk-card space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={t.status} label={t.status} /><PriorityPill priority={t.priority} />
          {t.slaBreached && <span className="text-xs text-critical">{tr('vendor:ticket.slaBreached')}</span>}
        </div>
        {t.description && <p className="text-sm text-muted">{t.description}</p>}
        {/* Location + Navigate */}
        {site && (
          <div className="flex items-center gap-2 border-t border-white/10 pt-3 text-sm">
            <Building2 size={14} className="text-muted" />
            <span>{site.name}{site.address ? ` · ${site.address}` : ''}</span>
            {site.lat != null && site.lng != null && (
              <a href={`https://maps.google.com/?q=${site.lat},${site.lng}`} target="_blank" rel="noreferrer" className="tk-btn-ghost px-2 py-0.5 text-xs">{tr('vendor:ticket.navigate', { defaultValue: 'Navigate' })}</a>
            )}
          </div>
        )}
        {/* Status actions — only the legal next transitions. */}
        <div className="flex flex-wrap gap-1.5 border-t border-white/10 pt-3">
          {nextStatuses.map((s) => (
            <button key={s} onClick={() => patch({ status: s })}
              className={`rounded-md px-2.5 py-1 text-xs ring-1 ring-white/10 text-muted hover:text-fg hover:bg-white/5 ${s === 'resolved' || s === 'closed' ? 'text-primary' : ''}`}>
              {tr(`vendor:status.${s}`)}
            </button>
          ))}
        </div>
        {/* Assign technician — opens the scored eligible-technician drawer. */}
        <div className="flex items-center gap-2 border-t border-white/10 pt-3 text-sm">
          <span className="text-muted">{tr('vendor:ticket.technician')}</span>
          <span className="font-medium">{t.assignedTechnicianId ? tr('vendor:ticket.assigned', { defaultValue: 'Assigned' }) : tr('vendor:ticket.unassigned')}</span>
          <button className="tk-btn-ghost px-2 py-1 text-xs" onClick={() => setAssignOpen(true)}>
            {t.assignedTechnicianId ? tr('vendor:ticket.reassign', { defaultValue: 'Reassign' }) : tr('vendor:ticket.assign', { defaultValue: 'Assign' })}
          </button>
        </div>
        {/* Comment */}
        <div className="flex gap-2 border-t border-white/10 pt-3">
          <input className="tk-input flex-1 py-1 text-sm" placeholder={tr('vendor:ticket.commentPlaceholder')} value={comment} onChange={(e) => setComment(e.target.value)} />
          <button className="tk-btn-ghost px-3 text-xs" disabled={!comment.trim()} onClick={async () => { await patch({ comment }); setComment(''); }}>{tr('vendor:ticket.addComment')}</button>
        </div>
      </div>
      {/* Activity log */}
      {Array.isArray(t.activityLog) && t.activityLog.length > 0 && (
        <div className="tk-card">
          <div className="mb-2 text-sm font-medium">{tr('vendor:ticket.activity')}</div>
          <ul className="space-y-1 text-xs text-muted">
            {(t.activityLog as Array<{ at?: string; actor?: string; text?: string; kind?: string }>).slice().reverse().map((a, i) => (
              <li key={i} className="border-b border-white/5 py-1 last:border-0">
                <span className="text-muted/60">{a.at ? new Date(a.at).toLocaleString() : ''}</span> · {a.actor} · {a.text ?? a.kind}
              </li>
            ))}
          </ul>
        </div>
      )}
      <AssignDrawer open={assignOpen} onClose={() => setAssignOpen(false)} tenantId={tenantId!} ticketId={id!}
        currentTechnicianId={t.assignedTechnicianId}
        onDone={() => { qclient.invalidateQueries({ queryKey: ['v-ticket', tenantId, id] }); qclient.invalidateQueries({ queryKey: ['v-tickets'] }); qclient.invalidateQueries({ queryKey: ['v-dispatch'] }); }} />
    </div>
  );
}

// Scored eligible-technician assign panel (mirrors the mobile assign sheet):
// eligible first (best score → Recommended), ineligible greyed with reasons.
function AssignDrawer({ open, onClose, tenantId, ticketId, currentTechnicianId, onDone }: {
  open: boolean; onClose: () => void; tenantId: string; ticketId: string; currentTechnicianId: string | null; onDone: () => void;
}) {
  const { t } = useTranslation(['vendor', 'common']);
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['v-eligible', tenantId, ticketId], queryFn: () => vendorApi.eligibleTechnicians(tenantId, ticketId), enabled: open });

  const assign = async (technicianId: string) => {
    setBusy(technicianId);
    try {
      const res = await vendorApi.assignTechnician(tenantId, ticketId, technicianId);
      toast.success(res.reassigned ? t('vendor:ticket.reassigned', { defaultValue: 'Reassigned' }) : t('vendor:ticket.assignedOk', { defaultValue: 'Assigned' }));
      onDone(); onClose();
    } catch (e) {
      const s = (e as { response?: { status?: number } })?.response?.status;
      toast.error(s === 409 ? t('vendor:ticket.assignRace', { defaultValue: 'Already assigned — refresh' }) : t('vendor:common.failed', { defaultValue: 'Could not complete' }));
    } finally { setBusy(null); }
  };

  return (
    <Drawer open={open} onClose={onClose} title={t('vendor:ticket.pickTech', { defaultValue: 'Choose technician' })} width="md">
      {isLoading ? <Skeleton className="h-48" /> : (data?.technicians ?? []).length === 0 ? (
        <EmptyState icon={Users} title={t('vendor:ticket.noEligible', { defaultValue: 'No technicians available' })} />
      ) : (
        <ul className="space-y-1">
          {(data?.technicians ?? []).map((tech) => {
            const isCurrent = tech.technicianId === currentTechnicianId;
            return (
              <li key={tech.technicianId}>
                <button disabled={isCurrent || busy !== null} onClick={() => assign(tech.technicianId)}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-start transition-colors ${tech.recommended ? 'bg-warning/10' : ''} ${isCurrent ? 'opacity-50' : 'hover:bg-white/5'} ${!tech.eligible ? 'opacity-55' : ''}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${tech.onShift ? 'bg-ok' : 'bg-muted/50'}`} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate font-medium">{tech.name}</span>
                      {tech.recommended && <span className="rounded bg-warning/20 px-1.5 text-[10px] text-warning">{t('vendor:ticket.recommended', { defaultValue: 'Recommended' })}</span>}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {[tech.onShift ? t('vendor:ticket.onShift', { defaultValue: 'on shift' }) : t('vendor:ticket.offShift', { defaultValue: 'off shift' }),
                        t('vendor:ticket.activeN', { defaultValue: '{{n}} active', n: tech.activeTickets }),
                        !tech.eligible && tech.rejectionReasons[0] ? tech.rejectionReasons[0] : null].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {tech.totalScore != null && <span className="tk-num text-sm font-semibold">{Math.round(tech.totalScore)}</span>}
                  {isCurrent && <span className="text-xs text-ok">✓</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Drawer>
  );
}

// ── Technicians ─────────────────────────────────────────────────────────────
function VendorTechniciansPage({ session }: { session: VendorSession }) {
  const { t } = useTranslation(['vendor', 'common']);
  const qclient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const { data: techs, isLoading } = useQuery({ queryKey: ['v-techs-all'], queryFn: () => vendorApi.technicians() });
  const orgOptions = useMemo(() => session.allowedTenantIds.map((id) => ({ value: id, label: id.slice(0, 8) })), [session.allowedTenantIds]);
  const columns: Column<VendorTechnician>[] = [
    { key: 'name', header: t('vendor:technicians.col.name'), className: 'font-medium' },
    { key: 'email', header: t('vendor:technicians.col.email'), render: (t2) => <span className="text-muted">{t2.email ?? '—'}</span> },
    { key: 'phone', header: t('vendor:technicians.col.phone'), render: (t2) => <span className="text-muted">{t2.phone ?? '—'}</span> },
    { key: 'tenantId', header: t('vendor:technicians.col.customer'), render: (t2) => <span className="text-muted">{t2.tenantId.slice(0, 8)}</span> },
    { key: 'status', header: t('vendor:technicians.col.status'), render: (t2) => <StatusPill status={t2.status} label={t2.status} /> },
  ];
  return (
    <div className="space-y-6">
      <PageHeader title={t('vendor:technicians.title')} subtitle={t('vendor:technicians.subtitle')} action={<button className="tk-btn-primary" onClick={() => setAddOpen(true)}>{t('vendor:technicians.add')}</button>} />
      {isLoading ? <Skeleton className="h-48" /> : (techs ?? []).length === 0 ? (
        <EmptyState icon={Users} title={t('vendor:technicians.empty.title')} hint={t('vendor:technicians.empty.hint')} />
      ) : <DataTable rows={techs ?? []} columns={columns} emptyIcon={Users} emptyTitle={t('vendor:technicians.empty.title')} />}
      {addOpen && (
        <QuickFormModal title={t('vendor:technicians.add')} submitLabel={t('vendor:form.add')}
          fields={[
            { key: 'tenantId', label: t('vendor:form.customer'), type: 'select', required: true, defaultValue: orgOptions[0]?.value, options: orgOptions },
            { key: 'name', label: t('vendor:form.name'), required: true },
            { key: 'email', label: t('vendor:form.email'), type: 'email' },
            { key: 'phone', label: t('vendor:form.phone') },
          ]}
          onClose={() => setAddOpen(false)}
          onSubmit={async (v) => { await vendorApi.createTechnician({ tenantId: v.tenantId, name: v.name, email: v.email || undefined, phone: v.phone || undefined }); qclient.invalidateQueries({ queryKey: ['v-techs-all'] }); }} />
      )}
    </div>
  );
}

// ── Teams (Phase 1) ─────────────────────────────────────────────────────────
// A vendor's technician teams, per customer org. A technician can belong to many
// teams; membership is the Phase-2/3 dispatch-eligibility foundation.
function VendorTeamsPage({ session }: { session: VendorSession }) {
  const { t } = useTranslation(['vendor', 'common']);
  const qclient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [memberFor, setMemberFor] = useState<VendorTeam | null>(null);
  const { data: teams, isLoading } = useQuery({ queryKey: ['v-teams-all'], queryFn: () => vendorApi.teams() });
  const orgOptions = useMemo(() => session.allowedTenantIds.map((id) => ({ value: id, label: id.slice(0, 8) })), [session.allowedTenantIds]);

  const columns: Column<VendorTeam>[] = [
    { key: 'name', header: t('vendor:teams.col.name', { defaultValue: 'Team' }), className: 'font-medium' },
    { key: 'description', header: t('vendor:teams.col.description', { defaultValue: 'Description' }), render: (r) => <span className="text-muted">{r.description ?? '—'}</span> },
    { key: 'tenantId', header: t('vendor:teams.col.customer', { defaultValue: 'Customer' }), render: (r) => <span className="text-muted">{r.tenantId.slice(0, 8)}</span> },
    { key: 'isActive', header: t('vendor:teams.col.status', { defaultValue: 'Status' }), render: (r) => <StatusPill status={r.isActive ? 'active' : 'inactive'} label={r.isActive ? 'active' : 'inactive'} /> },
    { key: 'id', header: '', render: (r) => <button className="tk-btn py-0.5 text-xs" onClick={() => setMemberFor(r)}>{t('vendor:teams.addMember', { defaultValue: 'Add member' })}</button> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('vendor:teams.title', { defaultValue: 'Teams' })}
        subtitle={t('vendor:teams.subtitle', { defaultValue: 'Organize technicians into dispatch teams per customer.' })}
        action={<button className="tk-btn-primary" onClick={() => setAddOpen(true)}>{t('vendor:teams.add', { defaultValue: 'New team' })}</button>} />
      {isLoading ? <Skeleton className="h-48" /> : (teams ?? []).length === 0 ? (
        <EmptyState icon={UsersRound} title={t('vendor:teams.empty.title', { defaultValue: 'No teams yet' })} hint={t('vendor:teams.empty.hint', { defaultValue: 'Create a team to group technicians.' })} />
      ) : <DataTable rows={teams ?? []} columns={columns} emptyIcon={UsersRound} emptyTitle={t('vendor:teams.empty.title', { defaultValue: 'No teams yet' })} />}

      {addOpen && (
        <QuickFormModal title={t('vendor:teams.add', { defaultValue: 'New team' })} submitLabel={t('vendor:form.add')}
          fields={[
            { key: 'tenantId', label: t('vendor:form.customer'), type: 'select', required: true, defaultValue: orgOptions[0]?.value, options: orgOptions },
            { key: 'name', label: t('vendor:form.name'), required: true },
            { key: 'description', label: t('vendor:teams.col.description', { defaultValue: 'Description' }) },
          ]}
          onClose={() => setAddOpen(false)}
          onSubmit={async (v) => { await vendorApi.createTeam({ tenantId: v.tenantId, name: v.name, description: v.description || undefined }); qclient.invalidateQueries({ queryKey: ['v-teams-all'] }); }} />
      )}

      {memberFor && <AddTeamMemberModal team={memberFor} onClose={() => setMemberFor(null)} onDone={() => qclient.invalidateQueries({ queryKey: ['v-teams-all'] })} />}
    </div>
  );
}

// Pick one of the vendor's technicians in the team's customer org and add them.
function AddTeamMemberModal({ team, onClose, onDone }: { team: VendorTeam; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation(['vendor', 'common']);
  const { data: techs } = useQuery({ queryKey: ['v-techs', team.tenantId], queryFn: () => vendorApi.technicians(team.tenantId) });
  const techOptions = (techs ?? []).map((tc) => ({ value: tc.id, label: tc.name }));
  if (!techs) return null;
  return (
    <QuickFormModal
      title={t('vendor:teams.addMemberTo', { defaultValue: 'Add to {{team}}', team: team.name })}
      submitLabel={t('vendor:form.add')}
      fields={[
        { key: 'technicianId', label: t('vendor:teams.technician', { defaultValue: 'Technician' }), type: 'select', required: true, defaultValue: techOptions[0]?.value, options: techOptions },
        { key: 'membershipType', label: t('vendor:teams.membershipType', { defaultValue: 'Membership' }), type: 'select', defaultValue: 'PRIMARY', options: [
          { value: 'PRIMARY', label: 'Primary' }, { value: 'SECONDARY', label: 'Secondary' }, { value: 'TEMPORARY', label: 'Temporary' },
        ] },
      ]}
      onClose={onClose}
      onSubmit={async (v) => { await vendorApi.addTeamMember(team.id, { tenantId: team.tenantId, technicianId: v.technicianId, membershipType: v.membershipType }); onDone(); onClose(); }} />
  );
}

// ── Shifts ──────────────────────────────────────────────────────────────────
function VendorShiftsPage({ session }: { session: VendorSession }) {
  const { t } = useTranslation(['vendor', 'common']);
  const qclient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const { data: shifts, isLoading } = useQuery({ queryKey: ['v-shifts'], queryFn: () => vendorApi.shifts() });
  const { data: techs } = useQuery({ queryKey: ['v-techs-all'], queryFn: () => vendorApi.technicians() });
  const orgOptions = session.allowedTenantIds.map((id) => ({ value: id, label: id.slice(0, 8) }));
  const techOptions = (techs ?? []).map((tech) => ({ value: tech.id, label: `${tech.name} (${tech.tenantId.slice(0, 8)})` }));
  const columns: Column<NonNullable<typeof shifts>[number]>[] = [
    { key: 'technicianId', header: t('vendor:shifts.col.technician'), render: (s) => (techs ?? []).find((tech) => tech.id === s.technicianId)?.name ?? s.technicianId.slice(0, 8) },
    { key: 'kind', header: t('vendor:shifts.col.type'), render: (s) => <span className="text-muted">{t(`vendor:shifts.kind.${s.kind}`, s.kind)}</span> },
    { key: 'startsAt', header: t('vendor:shifts.col.start'), className: 'tk-num', render: (s) => <span className="text-muted">{new Date(s.startsAt).toLocaleString()}</span> },
    { key: 'endsAt', header: t('vendor:shifts.col.end'), className: 'tk-num', render: (s) => <span className="text-muted">{new Date(s.endsAt).toLocaleString()}</span> },
    { key: 'tenantId', header: t('vendor:shifts.col.customer'), render: (s) => <span className="text-muted">{s.tenantId.slice(0, 8)}</span> },
  ];
  return (
    <div className="space-y-6">
      <PageHeader title={t('vendor:shifts.title')} subtitle={t('vendor:shifts.subtitle')} action={<button className="tk-btn-primary" onClick={() => setAddOpen(true)}>{t('vendor:shifts.add')}</button>} />
      {isLoading ? <Skeleton className="h-48" /> : (shifts ?? []).length === 0 ? (
        <EmptyState icon={CalendarClock} title={t('vendor:shifts.empty.title')} hint={t('vendor:shifts.empty.hint')} />
      ) : <DataTable rows={shifts ?? []} columns={columns} emptyIcon={CalendarClock} emptyTitle={t('vendor:shifts.empty.title')} />}
      {addOpen && (
        <QuickFormModal title={t('vendor:shifts.add')} submitLabel={t('vendor:form.add')}
          fields={[
            { key: 'tenantId', label: t('vendor:form.customer'), type: 'select', required: true, defaultValue: orgOptions[0]?.value, options: orgOptions },
            { key: 'technicianId', label: t('vendor:form.technician'), type: 'select', required: true, options: techOptions },
            { key: 'shiftType', label: t('vendor:shifts.col.type'), type: 'select', defaultValue: 'REGULAR', options: [
              { value: 'REGULAR', label: t('vendor:shifts.type.REGULAR', { defaultValue: 'Regular shift' }) },
              { value: 'ON_CALL', label: t('vendor:shifts.type.ON_CALL', { defaultValue: 'On-call' }) },
              { value: 'VACATION', label: t('vendor:shifts.type.VACATION', { defaultValue: 'Vacation' }) },
              { value: 'SICK', label: t('vendor:shifts.type.SICK', { defaultValue: 'Sick' }) },
              { value: 'UNAVAILABLE', label: t('vendor:shifts.type.UNAVAILABLE', { defaultValue: 'Unavailable' }) },
              { value: 'TRAINING', label: t('vendor:shifts.type.TRAINING', { defaultValue: 'Training' }) },
            ] },
            { key: 'startsAt', label: t('vendor:shifts.form.starts'), required: true, placeholder: '2026-06-09T08:00' },
            { key: 'endsAt', label: t('vendor:shifts.form.ends'), required: true, placeholder: '2026-06-09T16:00' },
            { key: 'repeat', label: t('vendor:shifts.repeat', { defaultValue: 'Repeat' }), type: 'select', defaultValue: 'once', options: [
              { value: 'once', label: t('vendor:shifts.repeatOnce', { defaultValue: 'Once' }) },
              { value: 'daily', label: t('vendor:shifts.repeatDaily', { defaultValue: 'Daily' }) },
              { value: 'weekly', label: t('vendor:shifts.repeatWeekly', { defaultValue: 'Weekly' }) },
            ] },
          ]}
          onClose={() => setAddOpen(false)}
          onSubmit={async (v) => {
            const startsAt = new Date(v.startsAt).toISOString();
            const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
            const recurrenceRule = v.repeat === 'daily' ? 'FREQ=DAILY'
              : v.repeat === 'weekly' ? `FREQ=WEEKLY;BYDAY=${WEEKDAYS[new Date(startsAt).getUTCDay()]}` : undefined;
            await vendorApi.createShift({
              tenantId: v.tenantId, technicianId: v.technicianId,
              kind: v.shiftType === 'ON_CALL' ? 'on_call' : 'shift', shiftType: v.shiftType,
              startsAt, endsAt: new Date(v.endsAt).toISOString(), recurrenceRule,
            });
            qclient.invalidateQueries({ queryKey: ['v-shifts'] });
          }} />
      )}
    </div>
  );
}

// ── Shift calendar (Phase 2) ────────────────────────────────────────────────
// A week grid: pick a customer org + week; shifts render as blocks under each
// day, conflicting shifts are highlighted, and active on-call rotations show
// who's on-call right now (computed server-side from the rotation).
const DAY_MS = 86_400_000;
function startOfWeek(d: Date): Date {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dow = x.getUTCDay(); // 0=Sun
  x.setUTCDate(x.getUTCDate() - dow);
  return x;
}
function VendorCalendarPage({ session }: { session: VendorSession }) {
  const { t } = useTranslation(['vendor', 'common']);
  const [tenantId, setTenantId] = useState(session.allowedTenantIds[0] ?? '');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const from = weekStart.toISOString();
  const to = new Date(weekStart.getTime() + 7 * DAY_MS).toISOString();

  const { data: cal, isLoading } = useQuery({
    queryKey: ['v-calendar', tenantId, from], enabled: !!tenantId,
    queryFn: () => vendorApi.shiftCalendar(tenantId, from, to),
  });
  const { data: techs } = useQuery({ queryKey: ['v-techs', tenantId], enabled: !!tenantId, queryFn: () => vendorApi.technicians(tenantId) });
  const { data: rotations } = useQuery({ queryKey: ['v-rotations', tenantId], enabled: !!tenantId, queryFn: () => vendorApi.rotations(tenantId) });

  const techName = (id: string) => (techs ?? []).find((x) => x.id === id)?.name ?? id.slice(0, 8);
  const conflictIds = useMemo(() => {
    const s = new Set<string>();
    (cal?.conflicts ?? []).forEach((c: ShiftConflict) => { if (c.aId) s.add(c.aId); if (c.bId) s.add(c.bId); });
    return s;
  }, [cal]);

  // Group shifts by day-of-week index (0..6) within the week.
  const byDay = useMemo(() => {
    const days: CalendarShift[][] = [[], [], [], [], [], [], []];
    (cal?.shifts ?? []).forEach((s: CalendarShift) => {
      const idx = Math.floor((new Date(s.startsAt).getTime() - weekStart.getTime()) / DAY_MS);
      if (idx >= 0 && idx < 7) days[idx].push(s);
    });
    return days;
  }, [cal, weekStart]);

  const dayLabel = (i: number) => new Date(weekStart.getTime() + i * DAY_MS).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const hhmm = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const absence = (s: CalendarShift) => ['VACATION', 'SICK', 'UNAVAILABLE'].includes(s.shiftType);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('vendor:calendar.title', { defaultValue: 'Shift calendar' })}
        subtitle={t('vendor:calendar.subtitle', { defaultValue: 'Weekly view of technician shifts, on-call, and conflicts.' })}
        action={
          <div className="flex items-center gap-2">
            <select className="tk-input py-1 text-sm" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
              {session.allowedTenantIds.map((id) => <option key={id} value={id}>{id.slice(0, 8)}</option>)}
            </select>
            <button className="tk-btn py-1 text-sm" onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * DAY_MS))}>←</button>
            <button className="tk-btn py-1 text-sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>{t('vendor:calendar.today', { defaultValue: 'This week' })}</button>
            <button className="tk-btn py-1 text-sm" onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * DAY_MS))}>→</button>
          </div>
        } />

      {/* On-call now panel */}
      {(rotations ?? []).length > 0 && (
        <div className="tk-card">
          <div className="mb-2 text-sm font-medium">{t('vendor:calendar.onCallNow', { defaultValue: 'On-call now' })}</div>
          <div className="flex flex-wrap gap-2">
            {(rotations ?? []).filter((r: Rotation) => r.isActive).map((r: Rotation) => <OnCallChip key={r.id} rotation={r} tenantId={tenantId} />)}
          </div>
        </div>
      )}

      {cal && cal.conflicts.length > 0 && (
        <div className="tk-card border-l-2 border-critical/60 text-sm">
          <span className="font-medium text-critical">{t('vendor:calendar.conflicts', { defaultValue: '{{n}} shift conflict(s)', n: cal.conflicts.length })}</span>
          <span className="ml-2 text-muted">{t('vendor:calendar.conflictsHint', { defaultValue: 'overlapping shifts for the same technician are highlighted below' })}</span>
        </div>
      )}

      {isLoading ? <Skeleton className="h-64" /> : (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-7">
          {byDay.map((shifts, i) => (
            <div key={i} className="tk-card min-h-[8rem] space-y-1 p-2">
              <div className="mb-1 text-xs font-medium text-muted">{dayLabel(i)}</div>
              {shifts.length === 0 ? <div className="text-[11px] text-muted/60">—</div> : shifts.map((s) => (
                <div key={s.id} className={`rounded px-1.5 py-1 text-[11px] leading-tight ${
                  conflictIds.has(s.id) ? 'bg-critical/15 text-critical ring-1 ring-critical/40'
                    : absence(s) ? 'bg-amber-500/15 text-amber-500'
                    : s.shiftType === 'ON_CALL' ? 'bg-primary/15 text-primary'
                    : 'bg-white/5 text-fg'}`}>
                  <div className="font-medium">{techName(s.technicianId)}</div>
                  <div className="text-muted">{hhmm(s.startsAt)}–{hhmm(s.endsAt)} · {s.shiftType.toLowerCase()}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Shows the currently on-call technician for one rotation (computed server-side).
function OnCallChip({ rotation, tenantId }: { rotation: Rotation; tenantId: string }) {
  const { data } = useQuery({ queryKey: ['v-oncall', rotation.id], queryFn: () => vendorApi.onCallNow(rotation.id, tenantId), refetchInterval: 60000 });
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs ring-1 ring-primary/30">
      <span className="font-medium text-primary">{rotation.name}</span>
      <span className="text-fg">{data?.name ?? '—'}</span>
    </span>
  );
}

// ── Operations dashboard (urgency + drill-in) ───────────────────────────────
function VendorDashboardPage() {
  const { t } = useTranslation(['vendor', 'common']);
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ['v-dashboard'], queryFn: vendorApi.dashboard, refetchInterval: 15000 });
  const q = data?.queue;
  // Compact ms → "12m" for the urgency hint.
  const ms = (v: number | null | undefined) => (v == null ? undefined : v < 3600_000 ? `${Math.round(v / 60000)}m` : `${Math.round(v / 3600000)}h`);
  const oldest = (v: number | null | undefined) => (ms(v) ? t('vendor:dashboard.oldest', { defaultValue: 'Oldest {{t}}', t: ms(v) }) : undefined);
  const nextBreach = (v: number | null | undefined) => (ms(v) ? t('vendor:dashboard.nextBreach', { defaultValue: 'Next {{t}}', t: ms(v) }) : undefined);
  return (
    <div className="space-y-6">
      <PageHeader title={t('vendor:dashboard.title', { defaultValue: 'Operations dashboard' })} subtitle={t('vendor:dashboard.subtitle', { defaultValue: 'Live dispatch queue, SLA risk, and technician status across your customers.' })} />
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <KpiStat label={t('vendor:dashboard.awaiting', { defaultValue: 'Awaiting acceptance' })} value={q?.awaitingAcceptance ?? '—'} tone={q && q.awaitingAcceptance > 0 ? 'warning' : 'ok'} hint={oldest(q?.oldestAwaitingMs)} onClick={() => navigate('/vendor/dispatch')} />
        <KpiStat label={t('vendor:dashboard.unassigned', { defaultValue: 'Unassigned' })} value={q?.unassigned ?? '—'} tone={q && q.unassigned > 0 ? 'warning' : 'ok'} onClick={() => navigate('/vendor/queue')} />
        <KpiStat label={t('vendor:dashboard.manual', { defaultValue: 'Manual action' })} value={q?.manualRequired ?? '—'} tone={q && q.manualRequired > 0 ? 'critical' : 'ok'} onClick={() => navigate('/vendor/queue')} />
        <KpiStat label={t('vendor:dashboard.atRisk', { defaultValue: 'SLA at risk' })} value={q?.atRisk ?? '—'} tone={q && q.atRisk > 0 ? 'warning' : 'ok'} hint={nextBreach(q?.nextBreachMs)} onClick={() => navigate('/vendor/queue')} />
        <KpiStat label={t('vendor:dashboard.breached', { defaultValue: 'SLA breached' })} value={q?.breached ?? '—'} tone={q && q.breached > 0 ? 'critical' : 'ok'} hint={oldest(q?.oldestBreachMs)} onClick={() => navigate('/vendor/queue')} />
        <KpiStat label={t('vendor:dashboard.pendingOffers', { defaultValue: 'Pending offers' })} value={data?.offers.pending ?? '—'} tone="info" hint={oldest(data?.offers.oldestPendingMs)} onClick={() => navigate('/vendor/dispatch')} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="tk-card">
          <div className="mb-3 text-sm font-medium">{t('vendor:dashboard.techStatus', { defaultValue: 'Technician status' })}</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(data?.techStatus ?? {}).length === 0 ? <span className="text-sm text-muted">—</span> :
              Object.entries(data?.techStatus ?? {}).map(([status, n]) => (
                <span key={status} className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs ring-1 ring-white/10">
                  <span className="font-medium">{status.toLowerCase()}</span><span className="text-muted">{n}</span>
                </span>
              ))}
          </div>
        </div>
        <div className="tk-card">
          <div className="mb-3 text-sm font-medium">{t('vendor:dashboard.byCustomer', { defaultValue: 'By customer' })}</div>
          <div className="space-y-2">
            {(data?.byCustomer ?? []).map((cust) => (
              <div key={cust.tenantId} className="flex items-center justify-between border-b border-white/5 py-2 text-sm last:border-0">
                <span className="flex items-center gap-2"><Building2 size={14} className="text-muted" /> {cust.tenantName || cust.tenantId.slice(0, 8)}</span>
                <span className="flex items-center gap-3 text-xs text-muted">
                  <span>{cust.open} open</span>
                  {cust.awaiting > 0 && <span className="text-warning">{cust.awaiting} awaiting</span>}
                  {cust.breaching > 0 && <span className="flex items-center gap-1 text-critical"><AlertTriangle size={12} /> {cust.breaching}</span>}
                </span>
              </div>
            ))}
            {(!data || data.byCustomer.length === 0) && <div className="text-sm text-muted">—</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Technician "My Work" — offers-first, real context, live countdown, decline
// reasons. (Web has no per-device technician-pin; identity is a picker when a
// vendor maps to several technicians — auto-selected when there's one.)
const TECH_STATUSES = ['AVAILABLE', 'DRIVING', 'ON_SITE', 'WORKING', 'ON_BREAK', 'OFF_SHIFT'];
const URGENCY = { normal: 'text-info', warning: 'text-warning', critical: 'text-critical', expired: 'text-muted' } as const;
function useOfferCountdown(expiresAt: string): { mmss: string; urgency: keyof typeof URGENCY } {
  const [ms, setMs] = useState(() => Math.max(0, new Date(expiresAt).getTime() - Date.now()));
  useEffect(() => { const id = setInterval(() => setMs(Math.max(0, new Date(expiresAt).getTime() - Date.now())), 1000); return () => clearInterval(id); }, [expiresAt]);
  const urgency = ms <= 0 ? 'expired' : ms <= 60_000 ? 'critical' : ms <= 5 * 60_000 ? 'warning' : 'normal';
  const s = Math.floor(ms / 1000);
  return { mmss: `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`, urgency };
}

function MyWorkPage({ session }: { session: VendorSession }) {
  const { t } = useTranslation(['vendor', 'common']);
  const navigate = useNavigate();
  const toast = useToast();
  const qclient = useQueryClient();
  const [tenantId, setTenantId] = useState(session.allowedTenantIds[0] ?? '');
  const { data: techs } = useQuery({ queryKey: ['v-techs', tenantId], enabled: !!tenantId, queryFn: () => vendorApi.technicians(tenantId) });
  const [technicianId, setTechnicianId] = useState('');
  const effTechId = technicianId || techs?.[0]?.id || '';
  const [declining, setDeclining] = useState<Offer | null>(null);
  const { data: work } = useQuery({
    queryKey: ['v-mywork', tenantId, effTechId], enabled: !!tenantId && !!effTechId,
    queryFn: () => vendorApi.myWork(tenantId, effTechId), refetchInterval: 10000,
  });
  const { data: reasons } = useQuery({ queryKey: ['v-reject-reasons'], queryFn: () => vendorApi.offerRejectReasons(), staleTime: 600_000, enabled: !!declining });
  const refresh = () => qclient.invalidateQueries({ queryKey: ['v-mywork', tenantId, effTechId] });

  const accept = async (o: Offer) => {
    try {
      const res = await vendorApi.acceptOffer(o.id, { tenantId, technicianId: effTechId, offerVersion: o.offerVersion });
      toast[res.accepted ? 'success' : 'info'](res.accepted ? t('vendor:myWork.accepted', { defaultValue: 'Offer accepted' }) : t('vendor:myWork.lost', { defaultValue: 'Offer no longer available' }));
    } catch { toast.info(t('vendor:myWork.lost', { defaultValue: 'Offer no longer available' })); }
    refresh();
  };
  const decline = async (reasonCode: string) => {
    if (!declining) return;
    await vendorApi.rejectOffer(declining.id, { tenantId, technicianId: effTechId, reasonCode });
    setDeclining(null); refresh();
    toast.success(t('vendor:myWork.declined', { defaultValue: 'Offer declined' }));
  };
  const setStatus = async (s: string) => { await vendorApi.setMyStatus({ tenantId, technicianId: effTechId, operationalStatus: s }); refresh(); };

  const assigned = work?.assigned ?? [];
  const current = assigned.find((j) => ['in_progress', 'on_hold', 'acknowledged'].includes(String(j.status))) ?? null;
  const rest = assigned.filter((j) => j.id !== current?.id);

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <PageHeader title={t('vendor:myWork.title', { defaultValue: 'My work' })} subtitle={t('vendor:myWork.subtitle', { defaultValue: 'Your offers, assignments, and status.' })} />

      <div className="flex flex-wrap items-center gap-2">
        {session.allowedTenantIds.length > 1 && (
          <select className="tk-input py-1 text-sm" value={tenantId} onChange={(e) => { setTenantId(e.target.value); setTechnicianId(''); }}>
            {session.allowedTenantIds.map((id) => <option key={id} value={id}>{id.slice(0, 8)}</option>)}
          </select>
        )}
        {(techs ?? []).length > 1 && (
          <select className="tk-input py-1 text-sm" value={effTechId} onChange={(e) => setTechnicianId(e.target.value)}>
            {(techs ?? []).map((tc) => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
          </select>
        )}
        {work && <span className="ml-auto text-xs text-muted">{work.onShift ? t('vendor:myWork.onShift', { defaultValue: 'On shift' }) : t('vendor:myWork.offShift', { defaultValue: 'Off shift' })}</span>}
      </div>

      {/* Pending offers (hero) */}
      {(work?.offers ?? []).map((o) => <OfferCard key={o.id} offer={o} onAccept={() => accept(o)} onDecline={() => setDeclining(o)} />)}

      {/* Current job */}
      {current && (<>
        <div className="text-sm font-medium">{t('vendor:myWork.current', { defaultValue: 'Current job' })}</div>
        <JobRow job={current} onOpen={() => navigate(`/vendor/tickets/${tenantId}/${current.id}`)} highlight />
      </>)}
      {/* Next jobs */}
      {rest.length > 0 && (<>
        <div className="text-sm font-medium">{t('vendor:myWork.next', { defaultValue: 'Next' })}</div>
        {rest.map((tk) => <JobRow key={tk.id} job={tk} onOpen={() => navigate(`/vendor/tickets/${tenantId}/${tk.id}`)} />)}
      </>)}
      {(work && work.offers.length === 0 && assigned.length === 0) && (
        <div className="tk-card text-center text-sm text-muted">{t('vendor:myWork.caughtUp', { defaultValue: "You're all caught up" })}</div>
      )}

      {/* Status control */}
      <div className="tk-card">
        <div className="mb-2 text-xs font-medium text-muted">{t('vendor:myWork.status', { defaultValue: 'Status' })}: <span className="text-fg">{work?.technician.operationalStatus?.toLowerCase() ?? '—'}</span></div>
        <div className="flex flex-wrap gap-2">
          {TECH_STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)} disabled={!effTechId}
              className={`rounded-full px-3 py-1 text-xs ${work?.technician.operationalStatus === s ? 'bg-primary/20 text-primary ring-1 ring-primary/40' : 'bg-white/5 text-muted hover:text-fg'}`}>
              {s.toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Decline reason sheet (Drawer) */}
      <Drawer open={!!declining} onClose={() => setDeclining(null)} title={t('vendor:myWork.declineTitle', { defaultValue: 'Decline offer' })} width="sm">
        <div className="mb-2 text-xs text-muted">{t('vendor:myWork.declineReason', { defaultValue: 'Reason' })}</div>
        <div className="flex flex-wrap gap-2">
          {(reasons ?? []).map((r) => (
            <button key={r.code} onClick={() => decline(r.code)} className="rounded-full bg-white/5 px-3 py-1.5 text-sm text-muted ring-1 ring-white/10 hover:bg-white/10 hover:text-fg">
              {t(`vendor:myWork.reason.${r.code}`, { defaultValue: r.label })}
            </button>
          ))}
        </div>
      </Drawer>
    </div>
  );
}

function OfferCard({ offer, onAccept, onDecline }: { offer: Offer; onAccept: () => void; onDecline: () => void }) {
  const { t } = useTranslation(['vendor', 'common']);
  const { mmss, urgency } = useOfferCountdown(offer.expiresAt);
  return (
    <div className={`tk-card space-y-3 ring-1 ${urgency === 'critical' ? 'ring-critical/50' : urgency === 'warning' ? 'ring-warning/50' : 'ring-info/40'}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted">{t('vendor:myWork.newAssignment', { defaultValue: 'New assignment' })}</span>
        <span className={`tk-num text-sm font-bold ${URGENCY[urgency]}`}>{mmss}</span>
      </div>
      <div className="flex items-center gap-2">
        <PriorityPill priority={offer.priority ?? 'medium'} />
        <span className="tk-num text-xs text-muted">{offer.ticketNumber}</span>
      </div>
      <div className="text-base font-medium">{offer.title ?? t('vendor:myWork.ticketOffer', { defaultValue: 'Ticket offer' })}</div>
      {offer.site?.name && <div className="text-xs text-muted">{offer.site.name}</div>}
      <div className="flex gap-2">
        <button className="tk-btn-primary flex-[2] py-2" onClick={onAccept}>{t('vendor:myWork.accept', { defaultValue: 'Accept' })}</button>
        <button className="tk-btn flex-1 py-2 text-critical" onClick={onDecline}>{t('vendor:myWork.decline', { defaultValue: 'Decline' })}</button>
      </div>
    </div>
  );
}

function JobRow({ job, onOpen, highlight }: { job: { id: string; ticketNumber?: string; title: string; status: string; priority: string; slaDeadline: string | null; site?: { name: string } | null }; onOpen: () => void; highlight?: boolean }) {
  return (
    <button onClick={onOpen} className={`tk-card flex w-full items-center gap-3 text-start hover:bg-white/5 ${highlight ? 'ring-1 ring-primary/30' : ''}`}>
      <PriorityPill priority={job.priority} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{job.ticketNumber ?? job.id.slice(0, 8)} · {job.title}</div>
        <div className="truncate text-xs text-muted">{job.site?.name ?? ''}{job.slaDeadline ? ` · SLA ${new Date(job.slaDeadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
      </div>
      <StatusPill status={job.status} label={job.status} />
    </button>
  );
}
